import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import capucinMonkey from '../assets/images/Capucin-monkey.webp';
import kinkajou from '../assets/images/Kinkajou.jpg';
import jaguar from '../assets/images/Jaguar.jpg';
import sloth from '../assets/images/Sloth.webp';
import backgroundImage from '../assets/images/rainforest-quiz-background.jpeg';

interface Question {
  id: number;
  text: string;
  options: {
    text: string;
    animal: string;
  }[];
}

interface Animal {
  name: string;
  description: string;
  image: string;
}

// Animal images mapping
const animalImages: Record<string, string> = {
  monkey: capucinMonkey,
  otter: kinkajou,
  jaguar: jaguar,
  sloth: sloth
};

const RainforestQuiz: React.FC = () => {
  const { t } = useTranslation();
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState<Record<number, string>>({});
  const [showResult, setShowResult] = useState(false);
  const [imageError, setImageError] = useState<Record<string, boolean>>({});

  // Get questions from translations
  const questions = [
    {
      id: 1,
      text: t('rainforestQuiz.questions.q1.question'),
      options: [
        { text: t('rainforestQuiz.questions.q1.answers.a'), animal: 'monkey' },
        { text: t('rainforestQuiz.questions.q1.answers.b'), animal: 'otter' },
        { text: t('rainforestQuiz.questions.q1.answers.c'), animal: 'jaguar' },
        { text: t('rainforestQuiz.questions.q1.answers.d'), animal: 'sloth' }
      ]
    },
    {
      id: 2,
      text: t('rainforestQuiz.questions.q2.question'),
      options: [
        { text: t('rainforestQuiz.questions.q2.answers.a'), animal: 'monkey' },
        { text: t('rainforestQuiz.questions.q2.answers.b'), animal: 'otter' },
        { text: t('rainforestQuiz.questions.q2.answers.c'), animal: 'jaguar' },
        { text: t('rainforestQuiz.questions.q2.answers.d'), animal: 'sloth' }
      ]
    },
    {
      id: 3,
      text: t('rainforestQuiz.questions.q3.question'),
      options: [
        { text: t('rainforestQuiz.questions.q3.answers.a'), animal: 'monkey' },
        { text: t('rainforestQuiz.questions.q3.answers.b'), animal: 'otter' },
        { text: t('rainforestQuiz.questions.q3.answers.c'), animal: 'jaguar' },
        { text: t('rainforestQuiz.questions.q3.answers.d'), animal: 'sloth' }
      ]
    },
    {
      id: 4,
      text: t('rainforestQuiz.questions.q4.question'),
      options: [
        { text: t('rainforestQuiz.questions.q4.answers.a'), animal: 'monkey' },
        { text: t('rainforestQuiz.questions.q4.answers.b'), animal: 'otter' },
        { text: t('rainforestQuiz.questions.q4.answers.c'), animal: 'jaguar' },
        { text: t('rainforestQuiz.questions.q4.answers.d'), animal: 'sloth' }
      ]
    }
  ];

  const progressPercentage = ((currentQuestion + 1) / questions.length) * 100;
  const progressLayer = currentQuestion < 1 ? 'canopy' : 
                       currentQuestion < 2 ? 'understory' : 
                       currentQuestion < 3 ? 'forest-floor' : 'roots';

  const handleImageError = (animal: string, error: any) => {
    console.error(`Error loading image for ${animal}:`, error);
    setImageError(prev => ({ ...prev, [animal]: true }));
  };

  const handleAnswer = (animal: string) => {
    setAnswers({ ...answers, [currentQuestion]: animal });
    if (currentQuestion < questions.length - 1) {
      setCurrentQuestion(currentQuestion + 1);
    } else {
      setShowResult(true);
    }
  };

  const getResult = () => {
    const animalCounts: Record<string, number> = {};
    Object.values(answers).forEach(animal => {
      animalCounts[animal] = (animalCounts[animal] || 0) + 1;
    });
    
    let maxCount = 0;
    let resultAnimal = '';
    
    Object.entries(animalCounts).forEach(([animal, count]) => {
      if (count > maxCount) {
        maxCount = count;
        resultAnimal = animal;
      }
    });
    
    return resultAnimal;
  };

  const restartQuiz = () => {
    setCurrentQuestion(0);
    setAnswers({});
    setShowResult(false);
  };

  if (showResult) {
    const resultAnimal = getResult();
    
    return (
      <div className="quiz-container" style={{
        backgroundImage: `url(${backgroundImage})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundBlendMode: 'overlay',
        backgroundColor: 'rgba(255, 255, 255, 0.75)'
      }}>
        <div className="quiz-result">
          <h2>{t(`rainforestQuiz.results.${resultAnimal}.title`)}</h2>
          <div className="result-image">
            {!imageError[resultAnimal] ? (
              <img 
                src={animalImages[resultAnimal]} 
                alt={t(`rainforestQuiz.results.${resultAnimal}.title`)}
                onError={(e) => handleImageError(resultAnimal, e)}
                onLoad={() => console.log(`Successfully loaded image for ${resultAnimal}`)}
              />
            ) : (
              <div className="image-placeholder">
                <span>🐾</span>
                <p className="error-message">Image not found: {animalImages[resultAnimal]}</p>
              </div>
            )}
          </div>
          <p>{t(`rainforestQuiz.results.${resultAnimal}.description`)}</p>
          <button className="quiz-button" onClick={restartQuiz}>
            {t('rainforestQuiz.playAgain')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="quiz-container" style={{
      backgroundImage: `url(${backgroundImage})`,
      backgroundSize: 'cover',
      backgroundPosition: 'center',
      backgroundBlendMode: 'overlay',
      backgroundColor: 'rgba(255, 255, 255, 0.75)'
    }}>
      <div className="quiz-progress">
        <div>{t('rainforestQuiz.progressPrefix')} {currentQuestion + 1} {t('rainforestQuiz.progressSuffix')} {questions.length}</div>
        <div className="progress-container">
          <div 
            className="progress-bar" 
            style={{ width: `${progressPercentage}%` }}
          />
          <div className="progress-layers">
            <div className={`progress-layer canopy ${progressLayer === 'canopy' ? 'active' : ''}`} />
            <div className={`progress-layer understory ${progressLayer === 'understory' ? 'active' : ''}`} />
            <div className={`progress-layer forest-floor ${progressLayer === 'forest-floor' ? 'active' : ''}`} />
            <div className={`progress-layer roots ${progressLayer === 'roots' ? 'active' : ''}`} />
          </div>
        </div>
        <div className="progress-text">
          {progressLayer === 'canopy' && "Exploring the Canopy"}
          {progressLayer === 'understory' && "Venturing into the Understory"}
          {progressLayer === 'forest-floor' && "Discovering the Forest Floor"}
          {progressLayer === 'roots' && "Uncovering the Roots"}
        </div>
      </div>
      <div className="quiz-question">
        <h2>{questions[currentQuestion].text}</h2>
        <div className="quiz-options">
          {questions[currentQuestion].options.map((option, index) => (
            <button
              key={index}
              className="quiz-button"
              onClick={() => handleAnswer(option.animal)}
            >
              {option.text}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

export default RainforestQuiz; 